/**
 * iter132 — Pending Treasury Actions banner.
 * iter284 — Multi-select + bulk approve; super admin can approve own actions
 * (matches backend rule).
 */
import { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { ShieldCheck, AlertTriangle, CheckCheck } from 'lucide-react';

function fmt(amount) {
  const n = Math.abs(Number(amount) || 0);
  return n.toLocaleString('en-IN');
}

function typeLabel(t) {
  if (t?.type === 'contribution' && t?.contribution_mode === 'sponsor') return 'Top-up fund';
  if (t?.type === 'coin_allot') return t?.amount >= 0 ? 'Allot Money' : 'Deduct ₹';
  if (t?.type === 'session_fee') return 'Session fee';
  return t?.type || 'Treasury action';
}

export default function PendingTreasuryBanner({
  items = [],
  currentUserId,
  isSuperAdmin,
  onApprove,
  onReject,
  onBulkApprove,
}) {
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  if (!items.length) return null;

  const canApproveItem = (t) => {
    const isMine = t.created_by === currentUserId;
    const needsSuper = t.requires_super_admin && !isSuperAdmin;
    return (!isMine || isSuperAdmin) && !needsSuper;
  };
  const approvableIds = items.filter(canApproveItem).map((t) => t.id);
  const allSelected = approvableIds.length > 0 && approvableIds.every((id) => selected.includes(id));

  const toggle = (id) => setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const toggleAll = () => setSelected(allSelected ? [] : approvableIds);

  const bulkApprove = async () => {
    if (!selected.length || !onBulkApprove) return;
    setBusy(true);
    try {
      await onBulkApprove(selected);
      setSelected([]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      className="p-3 border-2 border-amber-500/40 bg-amber-500/5 rounded-2xl space-y-2"
      data-testid="pending-treasury-banner"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <ShieldCheck className="w-4 h-4 text-amber-600" />
        <p className="text-sm font-bold flex-1">
          {items.length} treasury action{items.length > 1 ? 's' : ''} awaiting co-approval
        </p>
        {approvableIds.length > 1 && (
          <label className="flex items-center gap-1.5 text-xs font-semibold cursor-pointer select-none">
            <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="treasury-select-all" />
            Select all
          </label>
        )}
        {selected.length > 0 && (
          <Button
            size="sm"
            onClick={bulkApprove}
            disabled={busy}
            data-testid="treasury-bulk-approve"
            className="rounded-full h-8 text-xs px-3"
          >
            <CheckCheck className="w-3.5 h-3.5 mr-1" />
            Approve selected ({selected.length})
          </Button>
        )}
      </div>
      <div className="space-y-2">
        {items.map((t) => {
          const isMine = t.created_by === currentUserId;
          const needsSuper = t.requires_super_admin && !isSuperAdmin;
          const canApprove = canApproveItem(t);
          return (
            <div
              key={t.id}
              className="flex items-center justify-between gap-2 p-2 rounded-xl bg-background border"
              data-testid={`treasury-pending-row-${t.id}`}
            >
              {canApprove && (
                <Checkbox
                  checked={selected.includes(t.id)}
                  onCheckedChange={() => toggle(t.id)}
                  data-testid={`treasury-select-${t.id}`}
                  className="shrink-0"
                />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold truncate">
                  {typeLabel(t)} · ₹{fmt(t.amount)}
                  {t.user_name && ['coin_allot', 'session_fee'].includes(t.type) ? ` → ${t.user_name}` : ''}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {t.note ? `"${t.note}" · ` : ''}
                  by {isMine ? 'you' : (t.created_by_name || 'admin')}
                  {t.requires_super_admin && (
                    <span className="ml-1 inline-flex items-center gap-0.5 text-amber-700 dark:text-amber-300">
                      <AlertTriangle className="w-2.5 h-2.5" /> super-admin
                    </span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {canApprove ? (
                  <>
                    <Button
                      size="sm"
                      onClick={() => onApprove?.(t.id)}
                      data-testid={`treasury-approve-${t.id}`}
                      className="rounded-full h-8 text-xs px-3"
                    >
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onReject?.(t.id)}
                      data-testid={`treasury-reject-${t.id}`}
                      className="rounded-full h-8 text-xs px-3"
                    >
                      Reject
                    </Button>
                  </>
                ) : (
                  <span className="text-xs uppercase tracking-wider font-bold text-muted-foreground px-2">
                    {isMine ? 'Waiting for another admin' : 'Super-admin only'}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
